import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { useListSystemComponents } from 'src/features/systems/api/systemComponents/useListSystemComponents';
import CreateSystemComponentFormModal from 'src/features/systems/components/systemComponents/form/CreateSystemComponentFormModal';
import { SystemComponent } from 'src/features/systems/utils/types';
import {
    COMPONENT_PANEL,
    ComponentBadgeOptions,
    ComponentStatusBadgeOptions,
    ComponentTypeBadgeOptions,
} from 'src/features/systems/utils/constants';
import { systemsMessages } from 'src/features/systems/utils/messages';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import { CellBadge } from 'src/registry/components/table';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import { useSingleRowSelection } from 'src/registry/hooks/useSingleRowSelection';
import useSorting from 'src/registry/hooks/useSorting';
import { useTitleStore } from 'src/stores/useTitleStore';
import ComponentSidePanel from 'src/features/systems/components/systemComponents/ComponentSidePanel';
import CellComponentActions from 'src/features/systems/components/systemComponents/table/CellComponentActions';
import { generalMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import usePagination from 'src/registry/hooks/usePagination';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

const sortingOptions = {
    initialSortingState: [{ id: 'name', desc: false }],
};
const searchOptions = { globalFilterKey: 'name' };

const columnHelper = createColumnHelper<SystemComponent>();

function SystemComponents() {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const manageSystemComponents = useHasPermissions([PERMISSION.MANAGE_SYSTEM_COMPONENTS]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, parkingId, queryPaginationOptions],
    );

    const { data, isLoading, isError } = useListSystemComponents(payload);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(systemsMessages.components));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const singleRowSelection = React.useCallback(
        (rowId: string) => {
            setRowSelectionState({ [rowId]: true });
        },
        [setRowSelectionState],
    );

    const componentId = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelectionState)[0];
        if (!data || !firstSelected) return '';
        return data.results.find((row) => row.id === firstSelected)?.id ?? '';
    }, [data, rowSelectionState]);

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('component_type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                badgeOptions: ComponentTypeBadgeOptions,
                title: formatMessage(systemsMessages.type),
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('component', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                badgeOptions: ComponentBadgeOptions,
                title: formatMessage(systemsMessages.component),
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('code', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(systemsMessages.code),
            },
        }),
        columnHelper.accessor('zone', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: ({ row }) =>
                row.original.zone
                    ? row.original.zone.translations.find((translation) => translation.language === locale)?.name
                    : '-',
            meta: {
                title: formatMessage(systemsMessages.zone),
            },
        }),
        columnHelper.accessor('gateway', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: ({ row }) =>
                row.original.gateway
                    ? row.original.gateway.translations.find((translation) => translation.language === locale)?.name
                    : '-',
            meta: {
                title: formatMessage(systemsMessages.gateway),
            },
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: CellBadge,
            meta: {
                badgeOptions: ComponentStatusBadgeOptions,
                title: formatMessage(systemsMessages.status),
            },
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props) => (
                <CellComponentActions
                    {...props}
                    sidePanelName={COMPONENT_PANEL}
                    singleRowSelection={singleRowSelection}
                />
            ),
            maxSize: 45,
        }),
    ];

    return (
        <>
            <DataTable<SystemComponent, any>
                isLoading={isLoading}
                isError={isError}
                columns={columns}
                data={data}
                search={search}
                filters={filters}
                sorting={sorting}
                pagination={pagination}
                semantics={{
                    title: formatMessage(systemsMessages.components),
                    entryName: formatMessage(systemsMessages.component).toLowerCase(),
                    entryNames: formatMessage(systemsMessages.components).toLowerCase(),
                    placeholder: formatMessage(systemsMessages.component),
                }}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                    newEntry: manageSystemComponents ? CreateSystemComponentFormModal : undefined,
                }}
                getRowId={(row) => row.id.toString()}
            />
            <SidePanel onClose={clearSelection} name={COMPONENT_PANEL}>
                <ComponentSidePanel componentId={componentId} />
            </SidePanel>
        </>
    );
}

export default SystemComponents;
